import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormFieldsSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Form } from '@/components/ui-kit/form';
import { useEmployeeSnapshot } from '@/modules/service-desk/shared/employee';
import { translatedResolver } from '@/modules/service-desk/shared/form-resolver';
import { PhoneField, TextField } from '@/modules/service-desk/shared/components/form-fields';
import { useSubmitBusinessCard } from '../../hooks/use-business-card';
import {
  BusinessCardApplyValues,
  businessCardApplySchema,
} from '../../schemas/business-card-apply-form-schema';
import { BusinessCardApplication } from '../../types/business-card.types';

const EMPTY_VALUES: BusinessCardApplyValues = {
  name: '',
  designation: '',
  email: '',
  mobileNumber: '',
};

interface BusinessCardApplyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The application being edited; omitted for a new one. */
  existing?: BusinessCardApplication | null;
}

export const BusinessCardApplyDialog = ({
  open,
  onOpenChange,
  existing,
}: BusinessCardApplyDialogProps) => {
  const { t } = useTranslation();
  const { snapshot, isLoading: isProfileLoading } = useEmployeeSnapshot();
  const { mutateAsync: submit, isPending } = useSubmitBusinessCard();

  const form = useForm<BusinessCardApplyValues>({
    resolver: translatedResolver(businessCardApplySchema, t),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    if (existing) {
      form.reset({
        name: existing.Name ?? '',
        designation: existing.Designation ?? '',
        email: existing.Email ?? '',
        mobileNumber: existing.MobileNumber ?? '',
      });
    } else if (snapshot) {
      form.reset({
        name: snapshot.displayName,
        designation: snapshot.designation,
        email: snapshot.email,
        mobileNumber: snapshot.phoneNumber,
      });
    } else {
      form.reset(EMPTY_VALUES);
    }
  }, [open, existing, snapshot, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await submit({ existing: existing ?? undefined, values });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent alignCloseWithTitle className="grid-cols-[minmax(0,1fr)] sm:max-w-md">
        <DialogHeader className="pr-6">
          <DialogTitle className="break-words">
            {t(existing ? 'EDIT_BUSINESS_CARD_APPLICATION' : 'BUSINESS_CARD_APPLICATION_FORM')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* A new application is filled from the profile: placeholders until it loads. */}
            {!existing && isProfileLoading ? (
              <FormFieldsSkeleton fields={4} />
            ) : (
              <>
                <TextField control={form.control} name="name" required label={t('NAME')} />
                <TextField
                  control={form.control}
                  name="designation"
                  required
                  label={t('DESIGNATION')}
                />
                <TextField
                  control={form.control}
                  name="email"
                  required
                  type="email"
                  label={t('EMAIL')}
                />
                <PhoneField
                  control={form.control}
                  name="mobileNumber"
                  required
                  label={t('MOBILE_NUMBER')}
                />
              </>
            )}
            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              {/* A new application needs the applicant's profile; an edit does not. */}
              <Button
                type="submit"
                loading={isPending}
                disabled={isPending || (!existing && !snapshot)}
              >
                {t(existing ? 'UPDATE' : 'SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
